/**
 * ADR-0145 D1 — the `partId` namespace a CATALOG-resolved BOM leaf lives in.
 *
 * ## Why this is in `@misumi/shared` and not in `apps/api`
 *
 * D1 rules that `partId` stays a bare `string` and that the catalog arm is
 * distinguished by a SERVER-MINTED prefix rather than by a discriminated
 * reference type (the typed variant's blast radius was measured across ~14
 * `(partId: string, …)` SPA callbacks plus nine part/file-scoped routes, and
 * rejected). A bare string with a prefix convention only works if BOTH sides
 * read the convention from ONE definition — a second copy in `apps/web` is
 * exactly the duplicated-predicate defect ADR-0124 Gate-5 W1 recorded and
 * `staging-priceability.ts` exists to prevent. So the prefix and its two
 * predicates live here, next to that file, and are the only executable
 * occurrence.
 *
 * ## The disjointness property
 *
 * A staged-FILE `partId` is a `randomUUID()` (`uploads-routes.ts`), and that
 * population is what all nine file-scoped staging verbs key on. A collision
 * would make every one of them a silent, cross-tenant-shaped confused deputy —
 * and a `partId`-vs-`uploadId` confusion was PR #425's root cause, so this is a
 * demonstrated failure mode, not a hypothetical one.
 *
 * The prefix therefore contains `:`, a character the uuid grammar (`[0-9a-f-]`)
 * cannot produce. So no minted id can parse as a uuid and no uuid can carry the
 * prefix — a property of the ALPHABET, not of the data, which is why it is
 * testable as a property rather than sampled as a table. D1 requires that
 * assertion to exist in a TEST rather than a comment; it does, in
 * `apps/api/src/modules/parts-library/catalog-leaf-resolution.test.ts`.
 *
 * NOT to be confused with `namespaceNodeIdsBySession`'s use of the same
 * character: that namespaces a `nodeId` by SESSION and IS persisted
 * (`intake.staged_bom_node.id`). This namespaces a `partId` by RESOLUTION ARM
 * and is NEVER persisted — the durable value is
 * `intake.staged_bom_node.catalog_sku` (migration 1013), and the `partId` is
 * derived from it on every read.
 */
export const CATALOG_LEAF_PART_ID_PREFIX = 'cat:'

/** Mint the derived `partId` for a catalog-resolved BOM leaf. Total. */
export function mintCatalogLeafPartId(catalogSku: string): string {
  return `${CATALOG_LEAF_PART_ID_PREFIX}${catalogSku}`
}

/**
 * The ONE normalisation used for catalog-SKU IDENTITY: trimmed, upper-cased.
 *
 * ADR-0160 D2 — the real catalog's *canonicalised* SKU always wins, so a
 * decision recorded against `"hfs5-2020-600"` must still match the catalog's
 * `"HFS5-2020-600"`. The same rule is already applied for SKU identity by
 * `catalog-leaf-resolution.ts`' `candidatesFrom`/`resolveOne` and by
 * `node-match-state-projection.ts`; it lives here too because THIS is the file
 * the derivation itself lives in, and ADR-0161 D4 makes normalise-before-derive
 * a merge-blocking property rather than a convention.
 *
 * Deliberately does NOT strip interior whitespace: `"MSM A"` and `"MSMA"` are
 * different SKUs, and collapsing them would be the mirror image of the
 * double-minting defect this exists to prevent. Idempotent, so applying it to
 * an already-canonical SKU is a no-op.
 */
export function normalizeCatalogSku(catalogSku: string): string {
  return catalogSku.trim().toUpperCase()
}

/**
 * Mint the derived `partId` for a catalog SKU, **normalising first** — the
 * derivation the ORDER-PLACEMENT library writer uses (ADR-0161 D4/D18).
 *
 * ## Why this exists next to {@link mintCatalogLeafPartId} rather than replacing it
 *
 * ADR-0161 D4 requires the SKU→partId derivation to be *deterministic and
 * stable ACROSS CALL SITES*, and D4's partial unique index on
 * `(account_id, catalog_sku)` was REFUSED (amended D4), which makes that
 * property the SOLE protection for product decision 3 — one catalog library row
 * per tenant per SKU (D17 #3).
 *
 * {@link mintCatalogLeafPartId} is called by the catalog-leaf arm straight off a
 * real {@link CatalogItem}, whose SKU is already canonical. Normalisation is
 * idempotent, so for every real catalog SKU the two call sites derive the SAME
 * id — the agreement the dedup guarantee rests on. Changing the *raw* minter to
 * normalise would instead alter ids the confirm path already derives, which is a
 * data-migration question for existing rows (ADR-0161 Follow-up 5) and is
 * deliberately out of this story's scope.
 *
 * Both properties are pinned by the merge-blocking suite in
 * `catalog-leaf-part-id.test.ts`. Do not soften it.
 */
export function mintNormalizedCatalogLeafPartId(catalogSku: string): string {
  return mintCatalogLeafPartId(normalizeCatalogSku(catalogSku))
}

/**
 * Is this `partId` on the CATALOG resolution arm?
 *
 * The discriminant the data already carries, read the same way on both sides of
 * the wire. Consumers use it to decide what a row can be ASKED to do: a catalog
 * leaf has no `intake.staged_file` row, so every file-subject verb
 * (associate-drawing, generate-drawing, register-uploaded-drawing,
 * keep-uploaded) has no subject at all, and the part-subject mutation verbs have
 * no durable match-state home yet (migration 1013 DECISION 2 deferred it). Per
 * ADR-0144 D7, those controls are ABSENT rather than present-and-erroring.
 */
export function isCatalogLeafPartId(partId: string): boolean {
  return partId.startsWith(CATALOG_LEAF_PART_ID_PREFIX)
}

/**
 * The catalog SKU inside a minted `partId`, or `null` when the id is not on the
 * catalog arm. Only the FIRST separator delimits, so a SKU that itself contains
 * `:` round-trips.
 */
export function catalogLeafSkuFromPartId(partId: string): string | null {
  return isCatalogLeafPartId(partId) ? partId.slice(CATALOG_LEAF_PART_ID_PREFIX.length) : null
}
